import type { VenueType, PrimaryAtmosphere, Amenity } from '@wineryxplorer/shared';
import type { MapBounds } from '../../hooks/useSearchScope';
import { WishlistInBoundsChip } from '../WishlistInBoundsChip';
import { SortControls } from './SortControls';
import type { SortOption } from './SortControls';

interface ResultsHeaderProps {
  isPending: boolean;
  total: number | undefined;
  isMapAreaMode: boolean;
  regionName: string | undefined;
  cityName: string | undefined;
  hasUserLocation: boolean;
  hasActiveFilters: boolean;
  searchText: string;
  venueTypes: VenueType[];
  atmospheres: PrimaryAtmosphere[];
  amenities: Amenity[];
  onClearFilters: () => void;
  mapBounds: MapBounds | null;
  onShowMobileMap: () => void;
  sortOption: SortOption;
  onSortChange: (sort: SortOption) => void;
}

/**
 * Result-count summary, active-filter breadcrumb, wishlist chip, mobile map
 * toggle, and sort control — the band between the filters and the results.
 */
export const ResultsHeader = ({
  isPending,
  total,
  isMapAreaMode,
  regionName,
  cityName,
  hasUserLocation,
  hasActiveFilters,
  searchText,
  venueTypes,
  atmospheres,
  amenities,
  onClearFilters,
  mapBounds,
  onShowMobileMap,
  sortOption,
  onSortChange,
}: ResultsHeaderProps) => (
  <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-gray-100">
    <div className="text-sm text-gray-600">
      {isPending ? (
        'Searching...'
      ) : total !== undefined ? (
        <>
          <div>
            {total} winer{total === 1 ? 'y' : 'ies'}{' '}
            {isMapAreaMode
              ? 'in this area'
              : regionName
                ? `in ${regionName}`
                : cityName
                  ? `near ${cityName}`
                  : hasUserLocation
                    ? 'near you'
                    : 'matching your search'}
          </div>
          {hasActiveFilters && (
            <div className="text-xs text-gray-500 mt-0.5">
              Filtered by:{' '}
              {[
                regionName,
                cityName,
                searchText,
                ...venueTypes,
                ...atmospheres,
                ...amenities,
              ]
                .filter(Boolean)
                .join(' · ')}{' '}
              <button
                type="button"
                onClick={onClearFilters}
                className="text-primary-600 hover:text-primary-800 underline"
              >
                Clear all
              </button>
            </div>
          )}
        </>
      ) : null}
      <div className="mt-1">
        <WishlistInBoundsChip bounds={mapBounds} />
      </div>
    </div>
    <div className="flex items-center gap-2">
      {/* Mobile map toggle */}
      <button
        type="button"
        onClick={onShowMobileMap}
        className="lg:hidden inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
        Map
      </button>
      <SortControls
        value={sortOption}
        onChange={onSortChange}
      />
    </div>
  </div>
);
